<template>
  <div v-loading="loading && !errorText" class="redirect-page" :element-loading-text="$t('加载中，请稍后')">
    <el-empty
      v-if="errorText"
      class="home-empty"
      :image-size="185"
      :image="require('@/assets/404_images/empty.png')"
      :description="errorText"
    >
      <div slot="description" class="el-empty__description error-block">
        {{ errorText }}
        <el-button class="back-btn" type="primary" @click="backToPaas">{{ $t('返回重试') }}</el-button>
      </div>
    </el-empty>
  </div>
</template>

<script>
import db from '@klinx/utils/localstorage';
import { baseAuthorityApi, baseApplicationApi, baseTenantApi } from '@klinx/base/src/api/index.js';
import { APP_CONFIG_MAP, COMMON_ROUTES } from '../home/config';
import { mapGetters } from 'vuex';

export default {
  name: "RedirectPage",
  data() {
    return {
      loading: true,
      appVersion: '',
      errorText: ''
    };
  },
  computed: {
    ...mapGetters([
      'envConfig'
    ])
  },
  created() {
    // 记录paas平台来源
    db.save('platform', 'paas');
    window.sessionStorage.setItem('platform', 'paas');
  },
  async mounted() {
    let querySuccess = false;
    const { query } = this.$route;
    const { aid, code: paasCode, app_id: paasAppId } = query;

    // 获取登录token
    if (paasCode && paasAppId) {
      querySuccess = await this.onQueryLoginToken({ aid, paasCode, paasAppId });
    } else {
      this.errorText = this.$t('登录参数缺失，请返回重试或联系管理员');
      return;
    }

    // 查询应用菜单
    if (querySuccess) {
      this.appVersion = await this.getVersion();
      const appCode = await this.getAppCodeById(paasAppId);
      appCode ? await this.onAppQuery({
        code: appCode
      }) : this.errorText = this.$t('当前应用无匹配，请返回重试或联系管理员');
    } else {
      this.errorText = this.$t('获取登录参数失败，请返回重试或联系管理员');
    }
  },
  methods: {
    async getVersion() {
      const res = await baseTenantApi.getVersion();
      if (res.data.isSuccess) {
        return res.data.data || '';
      }
      return '';
    },
    /**
     * 跟进paas登录回跳带的参数，查登录token
     */
    async onQueryLoginToken(param) {
      const { aid, paasCode, paasAppId } = param;
      try {
        const res = await baseAuthorityApi.login({
          grantType: 'paassso',
          aid,
          paasCode,
          paasAppId
        }, { skipCheckToken: true });
        if (res.data?.isSuccess) {
          const {
            token,
            refreshToken,
            expiration,
            tenant,
            name,
            userId,
            avatar,
            account,
            workDescribe,
            paasExt: {
              tid,
              uid
            }
          } = res.data.data;
          this.$store.commit('account/setAppId', paasAppId);
          this.$store.commit('account/setTId', tid);
          this.$store.commit('account/setUId', uid);
          this.$store.commit('account/setToken', token);
          this.$store.commit('account/setRefreshToken', refreshToken);
          this.$store.commit('account/setExpireTime', expiration);
          this.$store.commit('account/setTenant', tenant);
          this.$store.commit('account/setUser', {
            id: userId,
            account: account,
            name: name,
            avatar: avatar,
            workDescribe: workDescribe
          });
          this.getResource();
          return true;
        }
        return false;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    getResource() {
      baseAuthorityApi.getResource().then(response => {
        const res = response.data;
        if (res.isSuccess) {
          const authorityResource = res.data;
          const permissionsList = authorityResource.resourceList || [];

          this.$store.commit('account/setPermissions', permissionsList);
          this.$store.commit('account/setAuthorityResource', authorityResource);
        }
      });
    },
    async getAppCodeById(appId) {
      try {
        const res = await baseApplicationApi.getAllApp({ skipCheckToken: true });
        if (res.data?.isSuccess) {
          this.$store.commit('account/setAppList', res.data.data);
          return res.data.data.filter(item => item.appId === appId)[0]?.code || '';
        }
        return '';
      } catch (e) {
        console.log(e);
        return '';
      }
    },
    /**
     * 查询应用（菜单等信息）
     */
    async onAppQuery(item) {
      try {
        let hasBase = false;
        let appName = '';
        const routerList = [];
        const res = await baseAuthorityApi.getRouter({
          appCode: item.code
        }, { skipCheckToken: true });

        if (!(res.data.isSuccess && res.data.data?.length)) {
          this.$message.error(this.$t('获取应用内菜单为空，请返回重试或联系管理员'));
          this.errorText = this.$t('获取应用内菜单为空，请返回重试或联系管理员');
          return;
        }

        res.data.data.forEach(app => {
          if ([item.code, 'base'].includes(app.appCode)) {
            app.appCode !== 'base' && app.children ? routerList.push(...app.children) : routerList.push(app);
          }
          if (app.appCode === 'base') {
            hasBase = true;
          }
          if (app.appCode === item.code) {
            appName = app.name;
          }
        });

        // 如果没有默认返回base菜单，则单独查
        if (!hasBase) {
          const baseRes = await baseAuthorityApi.getRouter({
            appCode: 'base'
          }, { skipCheckToken: true });
          baseRes.data.isSuccess && baseRes.data.data?.length && routerList.push(...baseRes.data.data);
        }

        if (this.setRouter(routerList)) {
          const homeUrl = routerList.filter(item => item.name === this.$t('首页'))[0]; // 菜单里有首页则跳首页
          const redirectPath = this.envConfig[`VUE_APP_${item.code}_REDIRECT_HOME`] || homeUrl?.path || '/dashboard';
          this.$store.commit('account/SET_CURRENT_APP', {
            ...item,
            isOpen: true,
            appName,
            version: this.appVersion || APP_CONFIG_MAP[item.code]?.version || 'v3.0.0'
          });
          document.title = appName;
          this.$router.replace({
            path: redirectPath
          });
        }
      } catch (e) {
        console.log(e);
        this.errorText = this.$t('获取应用内菜单出错，请返回重试或联系管理员');
      }
    },
    /**
     * 设置全局（应用）菜单路由
     */
    setRouter(routerList) {
      const commonRouter = [];
      const appRouter = [];
      let asyncRouter = [];
      routerList.forEach(item => {
        // 过滤非pc端的菜单
        if (item.group !== 'pc') {
          return;
        }

        if (COMMON_ROUTES.includes(item.path)) {
          commonRouter.push({
            ...item,
            isCommon: true
          });
        } else {
          appRouter.push(item);
        }
      });

      asyncRouter = [...appRouter, ...commonRouter];

      // 暂未配置菜单权限
      if (!asyncRouter.length) {
        this.$message.error(this.$t('当前租户暂未配置菜单权限，请联系管理员'));
        this.errorText = this.$t('当前租户暂未配置菜单权限，请联系管理员');
        return false;
      } else {
        asyncRouter.push({
          alwaysShow: false,
          component: 'error-page/404',
          hidden: false,
          name: '404',
          path: '*'
        });
        // 缓存
        this.$store.commit('account/setRoutes', asyncRouter);
      }
      return true;
    },
    backToPaas() {
      const defaultHome = `${window.location.protocol}//${window.location.hostname}:30068/`;
      window.location.replace(process.env.VUE_APP_PAAS_HOME_URL || defaultHome);
    }
  }
};
</script>

<style lang="scss" scoped>
.redirect-page {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  ::v-deep .error-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    .back-btn {
      margin-top: $spacing-m-16;
    }
  }
}
</style>
